import { useState, useEffect } from 'react';
import { useSubscription } from '@/hooks/useSubscription';

/**
 * Hook for user preferences that depend on subscription status.
 * Uses centralized useSubscription hook for consistent pro status checking.
 *
 * @param userId - The user ID (currently unused but kept for API compatibility)
 */
export function useUserPreferences(_userId: string) {
  const { hasActiveSubscription, profile, loading } = useSubscription();
  const [audioQuality, setAudioQuality] = useState<'mp3' | 'wav'>('wav');

  // Sync audio quality from profile when it loads
  useEffect(() => {
    if (profile?.audio_quality_preference) {
      setAudioQuality(profile.audio_quality_preference as 'mp3' | 'wav');
    }
  }, [profile?.audio_quality_preference]);

  // Listen for audio quality changes from settings
  useEffect(() => {
    const handleAudioQualityChange = (event: Event) => {
      const customEvent = event as CustomEvent<'mp3' | 'wav'>;
      setAudioQuality(customEvent.detail);
    };

    window.addEventListener('audioQualityChanged', handleAudioQualityChange);

    return () => {
      window.removeEventListener(
        'audioQualityChanged',
        handleAudioQualityChange
      );
    };
  }, []);

  // During loading, default to isPro=true (optimistic) to avoid flickering locked states
  // Once loaded, use actual subscription status
  const isPro = loading || hasActiveSubscription;

  return { isPro, audioQuality };
}
